import * as React from "react";
import { CalendarLtr20Regular } from "@fluentui/react-icons";
import { OpenWindowConfig } from "./WindowManagerContext";
import TerminFormWindow from "./TerminFormWindow";
import { zentrierteFensterPosition } from "./terminFormLogik";
import type { MailTerminLinkInput } from "../../services/calendarTerminService";
import type { TerminVorlage } from "../calendar/terminVorlage";

/**
 * Minimal-Prefill aus einem CalendarGridEvent (SP1) für den Edit-Modus —
 * vermeidet einen Doppel-Import des Grid-Event-Typs in das Fenster. Body und
 * strukturierte Teilnehmer lädt das Fenster bei Bedarf über getEvent nach.
 */
export interface TerminPrefill {
    id: string;
    title: string;
    start: Date;
    end: Date;
    allDay: boolean;
    location: string;
    mailbox: string;
    onlineMeetingUrl?: string | null;
}

/**
 * Parameter des Termin-Fensters. ALLEINIGE Quelle der Signatur (Konsistenz-
 * Review A1): SP3 (Drag-to-Create) nutzt initialStart/End/allDay/mailbox/
 * contactId, SP6 (FAB) ruft denselben Opener — keine eigenen Signaturen.
 * contactId ist `number | null` (NICHT string).
 */
export interface OpenTerminFormParams {
    // Neu-Anlage (Drag-to-Create-Brücke SP3 nutzt initialStart/End):
    initialStart?: Date;
    initialEnd?: Date;
    allDay?: boolean;
    /** Ziel-Postfach (Default: currentMailbox); bestimmt Edit-Ownership. */
    mailbox?: string | null;
    contactId?: number | null;
    contactName?: string | null;
    // Edit-Modus (stabiles Fenster termin-edit-<eventId>):
    editEventId?: string;
    prefillEvent?: TerminPrefill;
    /** false ⇒ read-only (fremder Kalender ohne WRITE). Default true. */
    canEdit?: boolean;
    /** Detail-Remake „Austausch": neu angelegten Termin mit diesem Vorgang
     *  verknüpfen (nur Create-Modus). */
    linkVorgangId?: number | null;
    /**
     * … und ihn gleich an DIESES To-do hängen (PO 18.09.2026, „Neuer Termin in
     * Aktionen"). Nur zusammen mit `linkVorgangId` sinnvoll: das Backend prüft,
     * dass To-do und Vorgang des Termins zusammenpassen. Ohne die Angabe hängt
     * der Termin am Vorgang — wie bisher.
     */
    linkTodoId?: number | null;
    /**
     * K1/J3 — den neu angelegten Termin automatisch mit DIESER Mail
     * verknüpfen. Setzt die Mail-Leiste, wenn der Nutzer aus dem
     * Verknüpfen-Kalender heraus „+ Neuer Termin" wählt.
     */
    linkMail?: MailTerminLinkInput | null;
    /** Betreff-Vorbelegung für die Neu-Anlage. */
    initialSubject?: string;
    /** Create (SP2, Review 2026-07-07): dezenter Hinweis im Formular, in
     *  WELCHEM Postfach angelegt wird — für Fallback-Fälle, in denen `mailbox`
     *  NICHT das aktuelle Postfach ist (kein WRITE ⇒ Default-WRITE-Postfach).
     *  Default false ⇒ alle Bestands-Aufrufer bleiben unverändert. */
    showMailboxHint?: boolean;
    /** Vg-SP6: vorbelegte Teilnehmer (NUR Create-Modus). Der Aufrufer liefert
     *  FERTIGE `{ address, name }` — kein Key-Resolving im Formular (vermeidet
     *  den Async-Race mit dem appUsers-Load). Default undefined ⇒ alle rund 15
     *  Bestands-Aufrufer bleiben unverändert. */
    initialAttendees?: { address: string; name?: string }[];
    /**
     * T-269 „Duplizieren…": NUR Neuanlage. Das Fenster lädt den Termin als
     * Vorlage (Teilnehmer, Ort, Raum, Kontakte, Art, Farbe, Vorgang …) und legt
     * beim Speichern einen NEUEN Termin an — nie ein PATCH auf die Vorlage.
     * Zeiten/Betreff gibt der Aufrufer über initialStart/End/allDay/
     * initialSubject mit, damit das Fenster sofort sinnvoll aussieht.
     */
    vorlage?: TerminVorlage;
}

/**
 * Öffnet ein Termin-Fenster (Neu oder Edit) — EINZIGE Öffnungs-Signatur; alle
 * rund 15 Aufrufer bleiben unverändert. Beide Modi laufen seit PO 28.09.2026
 * wieder im großen `TerminFormWindow` („das Termin-Popup muss wieder das alte
 * sein, das, was angezeigt wird, wenn man einen Termin ändern möchte"); der
 * schlanke Light-Dialog (SP5) ist entfallen.
 *   - Create ⇒ Fenster-ID `termin-create-<ts>` (jedes Mal neu).
 *   - Edit   ⇒ Fenster-ID `termin-edit-<editEventId>` (stabil → Re-Open fokussiert nur).
 * Auf dem Phone macht der WindowLayer daraus ein Vollbild-Panel.
 * Liefert die windowId zurück.
 */
export const openTerminFormWindow = (
    openWindow: (config: OpenWindowConfig) => void,
    params: OpenTerminFormParams = {},
): string => {
    const isEdit = !!params.editEventId;
    const windowId = isEdit
        ? `termin-edit-${params.editEventId}`
        : `termin-create-${Date.now()}`;

    const initialTitle = isEdit
        ? params.prefillEvent?.title?.trim() || "Termin"
        : params.vorlage
          ? "Neuer Termin (Kopie)"
          : "Neuer Termin";

    const initialSize = { width: 760, height: 720 };
    openWindow({
        id: windowId,
        title: initialTitle,
        icon: React.createElement(CalendarLtr20Regular),
        initialSize,
        // SP5/A7: zentriert öffnen statt Kaskaden-Offset — wirkt automatisch
        // für ALLE Aufrufer (auch „Termin anlegen" aus Vorgang-/Todo-Detail);
        // auf dem Phone rendert WindowLayer ohnehin Vollbild.
        initialPosition: zentrierteFensterPosition(
            { width: window.innerWidth, height: window.innerHeight },
            initialSize,
        ),
        render: () =>
            React.createElement(TerminFormWindow, {
                windowId,
                mode: isEdit ? "edit" : "create",
                initialStart: params.initialStart,
                initialEnd: params.initialEnd,
                allDay: params.allDay,
                mailbox: params.mailbox ?? null,
                contactId: params.contactId ?? null,
                contactName: params.contactName ?? null,
                editEventId: params.editEventId,
                prefillEvent: params.prefillEvent,
                canEdit: params.canEdit ?? true,
                linkVorgangId: params.linkVorgangId ?? null,
                // `linkTodoId` zählt nur in der Neuanlage: im Edit-Modus hängt
                // das To-do längst am Sidecar des Termins.
                linkTodoId: isEdit ? null : params.linkTodoId ?? null,
                linkMail: params.linkMail ?? null,
                initialSubject: params.initialSubject,
                initialAttendees: params.initialAttendees,
                vorlage: isEdit ? undefined : params.vorlage,
            }),
    });
    return windowId;
};

export default openTerminFormWindow;
